import { StyleShadowRoot } from "@redotech/react-util/style";
import { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import {
  LauncherType,
  SpotlightLauncher as SpotlightLauncherType,
  SpotlightTriggerType,
  TabOrientation,
  TabPlacement,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import { BrandKitButton } from "@redotech/redo-web/brand-kit-button";
import { Flex } from "@redotech/redo-web/flex";
import { StyleManager } from "@redotech/web-util/web-components/style";
import { memo, useEffect, useState } from "react";

export interface SpotlightLauncherProps {
  launcher: SpotlightLauncherType;
  onClick: () => void;
  brandKit?: BrandKit;
  triggerType?: SpotlightTriggerType;
  /**
   * Attach points provided by useBackInStockRoot when the launcher belongs
   * to a BACK_IN_STOCK spotlight. Used when launcher.anchorCssSelector is
   * empty so the embed launcher attaches to PDP add-to-cart forms.
   */
  pdpAttachPoints?: Element[];
  /**
   * Used in the anchor element id to isolate the launcher from other launchers.
   */
  spotlightId: string;
  /**
   * Style manager of the host page's Redo bundle. Embed launchers render
   * inside their own shadow roots, which need the Redo stylesheet registered.
   */
  styleManager: StyleManager;
}

export const SpotlightLauncher = memo(function SpotlightLauncher({
  launcher,
  onClick,
  brandKit,
  triggerType,
  pdpAttachPoints,
  spotlightId,
  styleManager,
}: SpotlightLauncherProps) {
  switch (launcher.type) {
    case LauncherType.TAB:
      return <TabLauncher launcher={launcher} onClick={onClick} />;
    case LauncherType.EMBED:
      return (
        <EmbedLauncher
          brandKit={brandKit}
          launcher={launcher}
          onClick={onClick}
          pdpAttachPoints={pdpAttachPoints}
          spotlightId={spotlightId}
          styleManager={styleManager}
          triggerType={triggerType}
        />
      );
    case LauncherType.BANNER:
      return <BannerLauncher launcher={launcher} onClick={onClick} />;
    default:
      return null;
  }
});

interface TabLauncherProps {
  launcher: Extract<SpotlightLauncherType, { type: "tab" }>;
  onClick: () => void;
}

const TabLauncher = memo(function TabLauncher({
  launcher,
  onClick,
}: TabLauncherProps) {
  const settings = launcher.advancedSettings;
  const placement = launcher.placement;
  const isLeftSide = [
    TabPlacement.TOP_LEFT,
    TabPlacement.CENTER_LEFT,
    TabPlacement.BOTTOM_LEFT,
  ].includes(placement);
  const isVerticalCenter = [
    TabPlacement.CENTER_LEFT,
    TabPlacement.CENTER_RIGHT,
  ].includes(placement);
  const isHorizontalCenter = [
    TabPlacement.TOP_CENTER,
    TabPlacement.BOTTOM_CENTER,
  ].includes(placement);
  const isVertical = launcher.orientation !== TabOrientation.HORIZONTAL;

  const positionStyles = getTabPositionStyles(placement);
  const rotateTransform = isVertical && isLeftSide ? "rotate(180deg)" : "none";
  const translateTransform = isVerticalCenter
    ? "translateY(-50%)"
    : isHorizontalCenter
      ? "translateX(-50%)"
      : "";
  const transform = `${translateTransform} ${rotateTransform}`.trim();

  const verticalStyles: React.CSSProperties = isVertical
    ? {
        minHeight: settings.width ? `${settings.width}px` : "auto",
        writingMode: "vertical-rl",
        textOrientation: "mixed",
      }
    : { minWidth: settings.width ? `${settings.width}px` : "auto" };

  return (
    <button
      onClick={onClick}
      style={{
        position: "fixed",
        zIndex: 9999,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: settings.background,
        color: settings.fontColor,
        border: `${settings.border.weight}px solid ${settings.border.color}`,
        borderRadius: `${settings.cornerRadius}px`,
        padding: `${settings.padding.top}px ${settings.padding.right}px ${settings.padding.bottom}px ${settings.padding.left}px`,
        ...verticalStyles,
        transform,
        fontFamily: launcher.fontFamily,
        fontSize: settings.fontSize,
        fontWeight: "var(--redo-font-weight-bold)",
        ...positionStyles,
      }}
      type="button"
    >
      {launcher.text}
    </button>
  );
});

function getTabPositionStyles(placement: TabPlacement): React.CSSProperties {
  const isLeftSide = [
    TabPlacement.TOP_LEFT,
    TabPlacement.CENTER_LEFT,
    TabPlacement.BOTTOM_LEFT,
  ].includes(placement);
  const horizontalPosition = isLeftSide ? { left: 8 } : { right: 8 };

  switch (placement) {
    case TabPlacement.TOP_LEFT:
    case TabPlacement.TOP_RIGHT:
      return { top: 20, ...horizontalPosition };
    case TabPlacement.CENTER_LEFT:
    case TabPlacement.CENTER_RIGHT:
      return { top: "50%", ...horizontalPosition };
    case TabPlacement.BOTTOM_LEFT:
    case TabPlacement.BOTTOM_RIGHT:
      return { bottom: 20, ...horizontalPosition };
    case TabPlacement.TOP_CENTER:
      return { top: 20, left: "50%" };
    case TabPlacement.BOTTOM_CENTER:
      return { bottom: 20, left: "50%" };
    default:
      return { bottom: 20, right: 8 };
  }
}

interface EmbedLauncherProps {
  launcher: Extract<SpotlightLauncherType, { type: "embed" }>;
  onClick: () => void;
  brandKit?: BrandKit;
  triggerType?: SpotlightTriggerType;
  pdpAttachPoints?: Element[];
  spotlightId: string;
  styleManager: StyleManager;
}

interface LauncherShadowHost {
  host: HTMLElement;
  shadowRoot: ShadowRoot;
}

const EmbedLauncher = memo(function EmbedLauncher({
  launcher,
  onClick,
  brandKit,
  triggerType,
  pdpAttachPoints,
  spotlightId,
  styleManager,
}: EmbedLauncherProps) {
  const [shadowHosts, setShadowHosts] = useState<LauncherShadowHost[]>([]);

  useEffect(() => {
    const anchors = ((): Element[] => {
      if (launcher.anchorCssSelector) {
        return Array.from(
          document.querySelectorAll(launcher.anchorCssSelector),
        );
      }
      if (
        triggerType === SpotlightTriggerType.BACK_IN_STOCK &&
        pdpAttachPoints
      ) {
        return pdpAttachPoints;
      }
      return [];
    })();

    // Need embeds to be in shadow roots. Otherwise, the launcher
    // inherits the theme and browser's default styles, including
    // padding around the p tag in the button label.
    const hosts = anchors.map((anchor, i) => {
      const host = document.createElement("div");
      host.id = `redo-spotlight-launcher-${spotlightId}-${i}`;
      host.style.display = "block"; // Prevent the anchor from being hidden by the browser for being "empty"
      anchor.append(host);
      return { host, shadowRoot: host.attachShadow({ mode: "open" }) };
    });
    setShadowHosts(hosts);

    return () => {
      for (const { host } of hosts) {
        host.remove();
      }
      setShadowHosts([]);
    };
  }, [launcher.anchorCssSelector, triggerType, pdpAttachPoints, spotlightId]);

  if (!brandKit) {
    return null;
  }

  return (
    <>
      {shadowHosts.map(({ host, shadowRoot }) => (
        <StyleShadowRoot
          key={host.id}
          shadowRoot={shadowRoot}
          styleManager={styleManager}
        >
          <Flex
            py="lg"
            style={{ width: `${launcher.width}px`, maxWidth: "100%" }}
          >
            <div style={{ width: "100%" }}>
              <BrandKitButton
                brandKit={brandKit}
                elementOverride={launcher.styleOverride}
                hierarchy={launcher.hierarchy}
                onClick={onClick}
                pending={false}
                text={launcher.text}
                type="button"
              />
            </div>
          </Flex>
        </StyleShadowRoot>
      ))}
    </>
  );
});

interface BannerLauncherProps {
  launcher: Extract<SpotlightLauncherType, { type: "banner" }>;
  onClick: () => void;
}

const BannerLauncher = memo(function BannerLauncher({
  launcher,
  onClick,
}: BannerLauncherProps) {
  const settings = launcher.advancedSettings;

  return (
    <button
      onClick={onClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        cursor: "pointer",
        backgroundColor: settings.background,
        color: settings.fontColor,
        border: `${settings.border.weight}px solid ${settings.border.color}`,
        borderRadius: `${settings.cornerRadius}px`,
        padding: `${settings.padding.top}px ${settings.padding.right}px ${settings.padding.bottom}px ${settings.padding.left}px`,
        textAlign: "center",
        fontSize: settings.fontSize,
        fontWeight: "var(--redo-font-weight-bold)",
      }}
      type="button"
    >
      {launcher.text}
    </button>
  );
});
